/**
 * Convert a Java doc comment to an HTML representation.
 *
 * The doc comment will always start with the characters / * and end in the characters * /.
 *
 * As the result is directly run as HTML, this function must sanitize the input appropriately.
 */
export function docCommentToHtml(comment: string): string {
	comment = comment.replace(/<script>/g, "&lt;script>");
	comment = comment.replace(/<img>/g, "&lt;script>");
	// Remove the comment characters
	comment = comment.replace(/\/\*\*/g, ""); // Remove the opening /**
	comment = comment.replace(/\/\*\*\s*<br>/g, "<br>"); // Remove the opening /** if it follows a <br> tag
	comment = comment.replace(/\*\//g, ""); // Remove the closing */
	comment = comment.replace(/\n\s*\*/g, "\n"); // Remove  * if it follows a <br> tag

	// Handle inline tags {@tag n}
	comment = comment.replace(/\{@code\s*(.*?)\s*\}/g, "<code>$1</code>");
	// Convert links to just text in their respective styles for now, no real way to open up the referenced classes yet
	comment = comment.replace(/\{@link\s*(.*?) (.*?)\s*\}/g, "<code>$2</code>");
	comment = comment.replace(
		/\{@linkplain\s*(.*?)\s*\}/g,
		'<a href="$1">$1</a>'
	);
	// @param e
	comment = comment.replace(
		/@param\s+([a-zA-Z0-9]*)\s*(.*?)\n/g,
		"<b><code>$1</code></b>: $2<br>"
	);
	// @return e
	comment = comment.replace(/@return\s*(.*?)\n/g, "<b>Returns</b>: $1<br>");
	comment = comment.replace(/@throws\s*(.*?)\n/g, "<b>Throws</b>: $1<br>");
	comment = comment.replace(
		/\{@inheritDoc\}/g,
		"<i>(Inherits from superclass)</i>"
	);
	comment = comment.replace(/\n\n/g, "<br>"); // Replace newlines with <br> tags
	// Split the comment into lines
	const lines = comment.split("<br>");
	// Remove the first line if it is empty
	if (lines[0]?.trim() === "") {
		lines.shift();
	}
	const authors: string[] = [];
	const sinceVersions: string[] = [];
	const seeReferences: string[] = [];
	const jlsReferences: string[] = [];
	const newLines: string[] = [];
	for (const line of lines) {
		const blockTag = captureBlockTagAndContent(line);
		switch (blockTag?.tag) {
			case "author":
				if (blockTag.content !== "unascribed") {
					authors.push(blockTag.content);
				}
				break;
			case "since":
				sinceVersions.push(blockTag.content);
				break;
			case "see":
				seeReferences.push(blockTag.content);
				break;
			case "jls":
				jlsReferences.push(blockTag.content);
				break;
			default:
				newLines.push(line);
		}
	}
	comment = newLines.join("<br>");
	// Construct tag section from block tags
	if (authors.length > 0) {
		comment += "<b>Author:</b>";
		comment += constructUnorderedList(authors);
	}
	if (sinceVersions.length > 0) {
		comment += "<b>Since:</b>";
		comment += constructUnorderedList(sinceVersions);
	}
	if (seeReferences.length > 0) {
		comment += "<b>See:</b>";
		comment += constructUnorderedList(seeReferences);
	}
	if (jlsReferences.length > 0) {
		comment += "<b>JLS:</b>";
		comment += constructUnorderedList(jlsReferences);
	}
	return comment;
}
/**
 * Construct an unordered list from a list of items.
 * @param items The items to construct the list from.
 * @returns The HTML representation of the unordered list.
 */
function constructUnorderedList(items: string[]): string {
	let result = "<ul>";
	for (const item of items) {
		result += `<li>${item}</li>`;
	}
	result += "</ul>";
	return result;
}
/**
 * Capture the block tag and its content from a line.
 * @param line The line to capture the block tag and content from.
 * @returns The tag and content if found, otherwise undefined.
 */
function captureBlockTagAndContent(line: string) {
	for (const tag of ["author", "since", "see", "jls"]) {
		const match = line.match(`@${tag} (.*)`);
		if (match && match[1]) {
			return {tag, content: match[1].trim()};
		}
	}
}
